/**
 * @fileoverview Premium Card Component for Skillzy.pro
 *
 * A glassmorphic card with multiple variants, hover effects, and
 * Framer Motion entrance animations.
 *
 * @example
 * <Card variant="elevated" hover>
 *   <Card.Header>
 *     <h3>Title</h3>
 *   </Card.Header>
 *   <Card.Body>Content here</Card.Body>
 *   <Card.Footer>Actions</Card.Footer>
 * </Card>
 *
 * <Card variant="gradient-border" padding="lg">
 *   Premium content
 * </Card>
 */

import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';

/** @type {Record<string, string>} */
const variantClasses = {
  default: clsx(
    'bg-white border border-slate-200/80',
    'shadow-sm',
    'dark:bg-slate-900 dark:border-slate-800'
  ),
  elevated: clsx(
    'bg-white border border-slate-200/60',
    'shadow-md shadow-slate-200/50',
    'dark:bg-slate-900 dark:border-slate-800 dark:shadow-slate-950/50'
  ),
  bordered: clsx(
    'bg-white border-2 border-slate-200',
    'dark:bg-slate-900 dark:border-slate-700'
  ),
  glass: clsx(
    'bg-white/80 backdrop-blur-xl border border-white/50',
    'shadow-sm',
    'dark:bg-slate-900/80 dark:border-slate-700/50'
  ),
  'gradient-border': clsx(
    'bg-white relative overflow-hidden',
    'dark:bg-slate-900',
    /* The gradient border is rendered via a pseudo-element, see CSS below */
    '[&]:before:absolute [&]:before:inset-0 [&]:before:p-[1px] [&]:before:rounded-[inherit]',
    '[&]:before:bg-gradient-to-br [&]:before:from-indigo-400 [&]:before:via-purple-500 [&]:before:to-pink-500',
    '[&]:before:-z-10'
  ),
  ghost: clsx(
    'bg-transparent border border-transparent',
    'hover:bg-slate-50 dark:hover:bg-slate-800/50'
  ),
  flat: clsx(
    'bg-slate-50 border border-transparent',
    'dark:bg-slate-800/50'
  ),
};

/** @type {Record<string, string>} */
const paddingClasses = {
  none: '',
  xs: 'p-3',
  sm: 'p-4',
  md: 'p-5 sm:p-6',
  lg: 'p-6 sm:p-8',
  xl: 'p-8 sm:p-10',
};

/** @type {Record<string, string>} */
const radiusClasses = {
  sm: 'rounded-lg',
  md: 'rounded-xl',
  lg: 'rounded-2xl',
  xl: 'rounded-3xl',
};

const hoverClasses = clsx(
  'transition-all duration-300 ease-out',
  'hover:shadow-lg hover:border-slate-300/80 hover:-translate-y-0.5',
  'dark:hover:border-slate-700 dark:hover:shadow-slate-950/50'
);

/**
 * Premium Card component with glassmorphism and gradient border variants.
 *
 * @param {Object} props
 * @param {'default' | 'elevated' | 'bordered' | 'glass' | 'gradient-border' | 'ghost' | 'flat'} [props.variant='default']
 * @param {'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl'} [props.padding='md']
 * @param {'sm' | 'md' | 'lg' | 'xl'} [props.radius='md']
 * @param {boolean} [props.hover=false] - Enable hover lift effect
 * @param {boolean} [props.animate=false] - Enable entrance animation
 * @param {string} [props.className] - Additional CSS classes
 * @param {React.ReactNode} props.children
 * @param {Function} [props.onClick] - Click handler
 */
const Card = forwardRef(({
  variant = 'default',
  padding = 'md',
  radius = 'md',
  hover = false,
  animate = false,
  className,
  children,
  onClick,
  ...rest
}, ref) => {
  const Component = animate ? motion.div : 'div';

  const animationProps = animate ? {
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.4, ease: [0, 0, 0.2, 1] },
  } : {};

  const isGradientBorder = variant === 'gradient-border';

  return (
    <Component
      ref={ref}
      className={clsx(
        'relative',
        radiusClasses[radius] || radiusClasses.md,
        variantClasses[variant] || variantClasses.default,
        paddingClasses[padding],
        hover && hoverClasses,
        onClick && 'cursor-pointer',
        'transition-colors duration-200',
        className
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      {...animationProps}
      {...rest}
    >
      {/* Gradient border overlay for gradient-border variant */}
      {isGradientBorder && (
        <div
          className={clsx(
            'absolute inset-0 rounded-[inherit] p-px',
            'bg-gradient-to-br from-indigo-400 via-purple-500 to-pink-500',
            'pointer-events-none',
            '-z-10'
          )}
          aria-hidden="true"
        >
          <div className={clsx(
            'h-full w-full rounded-[inherit]',
            'bg-white dark:bg-slate-900'
          )} />
        </div>
      )}
      {children}
    </Component>
  );
});

Card.displayName = 'Card';

/**
 * Card.Header - Top section with optional bottom border
 */
const CardHeader = forwardRef(({ className, border = false, title, subtitle, children, ...rest }, ref) => (
  <div
    ref={ref}
    className={clsx(
      'flex items-center justify-between',
      border && 'border-b border-slate-200 dark:border-slate-800 pb-4 mb-4',
      className
    )}
    {...rest}
  >
    {(title || subtitle) ? (
      <div>
        {title && <h3 className="text-lg font-bold text-slate-900 dark:text-white">{title}</h3>}
        {subtitle && <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
    ) : null}
    {children}
  </div>
));

CardHeader.displayName = 'CardHeader';

/**
 * Card.Body - Main content area
 */
const CardBody = forwardRef(({ className, children, ...rest }, ref) => (
  <div
    ref={ref}
    className={clsx('flex-1', className)}
    {...rest}
  >
    {children}
  </div>
));

CardBody.displayName = 'CardBody';

/**
 * Card.Footer - Bottom section with optional top border
 */
const CardFooter = forwardRef(({ className, border = false, children, ...rest }, ref) => (
  <div
    ref={ref}
    className={clsx(
      'flex items-center justify-end gap-3',
      border && 'border-t border-slate-200 dark:border-slate-800 pt-4 mt-4',
      className
    )}
    {...rest}
  >
    {children}
  </div>
));

CardFooter.displayName = 'CardFooter';

// Attach subcomponents
Card.Header = CardHeader;
Card.Body = CardBody;
Card.Footer = CardFooter;

export default Card;
